import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import type { CrawlRunPeriodSource } from './crawl-run-period.service';

/**
 * The Impact Brief — this period's work packages (IMPACT_BRIEF_PLAN.md).
 *
 * Mirrors the IB1 composer payload (`backend/src/services/impact-brief-packages.ts`).
 * Everything here is read and rendered: the grouping, ranking, caps and copy are
 * the backend's, per locked decision 1 (deterministic packaging) and 9 (one copy
 * module). Angular must not re-derive a package, re-sort one, or write its own
 * title — Dashboard and Issues render the same server strings so the two
 * surfaces cannot disagree about the week.
 */

/** Ladder 2 (crawl pair) vs ladder 4 (no pair) — DASHBOARD_HOME.md §2. */
export type ImpactBriefMode = 'monitoring' | 'issues-potential';

/**
 * The crawl pair this brief was measured over — ids and stamps only, the same
 * frozen pair Issues and LB5 use. It carries no label: LB1 owns how a period is
 * *named*, and `periodSource` below is what the UI actually renders from.
 */
export interface ImpactBriefPeriod {
  currentCrawlRunId: string;
  previousCrawlRunId: string;
  currentCapturedAt: string | null;
  previousCapturedAt: string | null;
}

export interface ImpactBriefHeadline {
  /** Fixed-template operator copy, composed server-side. Rendered verbatim. */
  text: string;
  /**
   * Null and 0 are different answers: null is `issues-potential` mode, where no
   * pair was ever measured; 0 is a measured period whose decay was SERP-only.
   * Do not collapse them.
   */
  trafficDecaySum: number | null;
  gapSum: number | null;
  /** Pages across all shown packages. */
  pageCount: number;
  /**
   * How many pages Review actually walks — see the backend's note.
   *
   * Different from `pageCount` whenever the brief holds a link package or lists
   * one URL twice, and optional because a brief served before this field
   * existed does not carry it. Callers fall back to `pageCount`, which is what
   * they used before and is right whenever the two agree.
   */
  reviewablePageCount?: number;
  decayingPageCount: number;
}

export type ImpactPackageKind = 'action-cluster' | 'decay-prefix' | 'link';

export interface ImpactPackagePeriodChange {
  direction: 'decay' | 'win';
  metricLabel: string;
  change: number;
  /** The diff layer's own rendering. Render as-is — never re-subtract. */
  display: string;
  /** The LB5 one-liner already on the Issues card. */
  evidence: string;
}

/**
 * Where a page's `keyword` came from — a measured query, or an unmeasured one.
 *
 * `target-query` is what the live SERP was fetched for, so the page's position
 * and KD belong to it. `claim` is the demand label on a page whose SERP is
 * still pending: the same kind of phrase, with nothing measured against it yet.
 */
export type ImpactPackageKeywordSource = 'target-query' | 'claim';

export interface ImpactPackagePage {
  urlId: string;
  url: string;
  /** Precomputed server-side (pathname only). Do not derive. */
  path: string;
  gap: number | null;
  realizationRatio: number | null;
  estimatedOrganicTraffic: number | null;
  demandClaim: string | null;
  /**
   * The keyword this page competes for, precomputed server-side like `path`.
   * NOT `clusterLabel` — that is the grouping of keywords, not a keyword.
   */
  keyword: string | null;
  /** Null exactly when `keyword` is null. */
  keywordSource: ImpactPackageKeywordSource | null;
  priorityScore: number;
  periodPriorityScore: number;
  clusterLabel: string | null;
  /** Already sent as work, or put aside, this period. */
  decided?: boolean;
  /**
   * The title this page is reviewed under, server-side (§0bf): the package
   * title unless that title claims a loss this page did not have.
   */
  title?: string;
  /** What the page did, when the package title says otherwise. Null otherwise. */
  standing?: string | null;
  /** Null when the page did not move this period. */
  periodChange: ImpactPackagePeriodChange | null;
}

export interface ImpactPackageLink {
  suggestionKey: string;
  sourceUrlId: string;
  sourceUrl: string;
  sourcePath: string;
  targetUrlId: string;
  targetUrl: string;
  targetPath: string;
  suggestedAnchor: string;
  score: number;
}

export interface ImpactPackage {
  /**
   * Stable for a domain + period (`actionType::cluster:<label>`,
   * `decay-prefix:<prefix>`, `link::cluster:<label>`) — no run ids, no
   * timestamps. This is the id Issues must match (plan §0 trust bar).
   */
  id: string;
  kind: ImpactPackageKind;
  title: string;
  /** One operator sentence, with the numbers already in it. */
  why: string;
  actionType: string;
  clusterLabel: string | null;
  /** Set on `kind: 'decay-prefix'` (e.g. `/templates`), else null. */
  pathPrefix: string | null;
  /** FULL set size, not `pages.length`. */
  pageCount: number;
  /** Capped at `caps.maxPagesPerPackage`. */
  pages: ImpactPackagePage[];
  /** `pageCount - pages.length`, computed server-side. */
  pagesTruncated: number;
  /**
   * Pages in this package already decided this period — put aside, or worked
   * until nothing was left to send.
   *
   * Counted server-side against the FULL row set, for the same reason
   * `reviewablePageCount` is: this surface receives five pages and would report
   * "2 of 5 done" for a package of eighteen. Optional so a pre-IB backend
   * simply shows no progress rather than showing zero progress.
   */
  decidedPageCount?: number;
  /**
   * Nothing left to review here. Always false on a `link` package, which has no
   * page decisions and so can never finish.
   */
  completed?: boolean;
  gapSum: number;
  /**
   * Absolute est. organic traffic lost, positive. Can be 0 on a package that is
   * genuinely decaying — pages that slipped on SERP position cost no measurable
   * traffic. Read alongside `decayingPageCount`, never instead of it.
   */
  decaySum: number;
  decayingPageCount: number;
  rankScore: number;
  /** Uncapped total for `kind: 'link'`; null otherwise. */
  linkCount: number | null;
  /**
   * Capped at `caps.maxLinksPerPackage`, in whole source pages, each page's
   * links together; null otherwise.
   */
  links: ImpactPackageLink[] | null;
  linksTruncated: number | null;
  /** Source pages with a link the cap hid. `link` packages only (§0az). */
  linkPagesTruncated?: number;
}

export interface ImpactBriefCaps {
  maxPackages: number;
  maxPagesPerPackage: number;
  maxLinksPerPackage: number;
}

export interface ImpactBriefTotals {
  openQueueItems: number;
  packagedPageCount: number;
  excludedPageCount: number;
  /** Analysed pages kept out of the queue — not live in the latest crawl (§0bf). */
  leftOutPageCount?: number;
  /** The count with its reasons, composed server-side. Null when none. */
  leftOutNote?: string | null;
}

/**
 * Why `packages` is empty, when it is.
 *
 * `no_analysis` — domain resolved but never analysed. `openQueueItems` is 0 and
 * Setup is the honest next step.
 * `no_packages` — the queue has rows (`openQueueItems > 0`), none of them
 * package-shaped. Here a Setup call to action would be wrong: the work exists
 * and it is in Issues.
 *
 * Null on the payload means not empty; a null *payload* is a third case again,
 * a workspace with nothing to plan for at all. The three want different copy.
 */
export type ImpactBriefEmptyState = 'no_analysis' | 'no_packages';

/**
 * One thing the week plan is built from, and whether it has arrived.
 *
 * Mirrors `backend/src/services/impact-brief-readiness.ts`. `covered` and
 * `expected` are null on a signal that is a yes/no, and — separately — on a run
 * whose per-signal coverage was never recorded. Null is "unknown", never zero.
 */
export interface ImpactBriefReadinessSignal {
  key: 'core-pipeline' | 'ranked-keywords' | 'serp' | 'opportunities';
  label: string;
  /** What the denominator counts — not always "pages". */
  unit: string;
  ready: boolean;
  covered: number | null;
  expected: number | null;
}

/**
 * Whether the data behind this plan has finished arriving (§0ax).
 *
 * **Disclosure, not gating.** Nothing on this object may hide a package, shrink
 * the belt or hold the period back — it is rendered and nothing else. An
 * incomplete brief is still the operator's brief; they just get told it is
 * still filling in. Gating is the right end state and waits on §0aw.
 */
export interface ImpactBriefReadiness {
  complete: boolean;
  /**
   * Work is genuinely still running. False with `complete: false` is the
   * normal shape of a finished domain — coverage never reaches every page —
   * which is why the summary below, not this flag, is what gets rendered.
   */
  building: boolean;
  signals: ImpactBriefReadinessSignal[];
  /** Composed server-side, so both surfaces say the same words. */
  summary: string | null;
}

export interface ImpactBriefPayload {
  domain: string;
  generatedAt: string;
  /** `monitoring` iff a real crawl pair exists. */
  mode: ImpactBriefMode;
  period: ImpactBriefPeriod | null;
  /**
   * Shaped to drop straight into `buildCrawlRunPeriodView` so the brief and the
   * movers name the period with one vocabulary. `state` of `absent`/`first-run`
   * implies `mode: 'issues-potential'`.
   */
  periodSource: CrawlRunPeriodSource;
  /** Null on a brief composed without one — render nothing, as before. */
  readiness: ImpactBriefReadiness | null;
  headline: ImpactBriefHeadline;
  /** Already capped and already sorted — render in order. */
  packages: ImpactPackage[];
  /** Set when `packages` is empty, explaining which empty this is. */
  emptyState: ImpactBriefEmptyState | null;
  /** Echoes the composer's exported constants so the UI hardcodes no cap. */
  caps: ImpactBriefCaps;
  totals: ImpactBriefTotals;
}

@Injectable({ providedIn: 'root' })
export class ImpactBriefService {
  private readonly API_URL = environment.apiUrl + '/v1';

  constructor(private http: HttpClient) {}

  /**
   * This period's packages. One route for every surface.
   *
   * `domain` is a required argument but may be null. Passing null lets the
   * server resolve the workspace's dashboard domain the way
   * `/urls/dashboard-movers` does; passing a domain pins the answer to it.
   * Callers must decide explicitly, because a multi-domain workspace where one
   * surface pins the domain and another lets the server pick can render two
   * different package sets and quietly break the trust bar the plan is built on.
   * Prefer passing the domain the surface is already displaying.
   *
   * Deliberately uncached, unlike `CrawlRunPeriodService` — hence no `refresh`
   * option to get wrong. A capture turns the period over and recomposes every
   * package, and a cached brief rendered against a fresh queue would break that
   * same trust bar in exactly the situation where an operator would believe the
   * stale answer.
   *
   * A failed read resolves to null rather than erroring: the packages are the
   * skim, and losing them must not take the movers or the rest of the dashboard
   * down with them.
   *
   * That covers the feature flag too. The route sits behind
   * `assertActionPlanV2Enabled()` and 403s when `ACTION_PLAN_V2_ENABLED` is off,
   * which lands here as null and renders as no packages section at all — a
   * flagged-off surface should look absent, not broken. Callers must therefore
   * treat null as "nothing to show" and never as an error worth reporting.
   */
  getBrief(
    domain: string | null,
    options: { expandedPackageIds?: string[]; expandAll?: boolean } = {}
  ): Observable<ImpactBriefPayload | null> {
    const key = (domain || '').trim();
    // Named package ids, comma-joined: the operator opened "+13 more pages in
    // this package". The server keeps the cap for every package NOT named, so
    // one opened package cannot turn the brief into the full queue.
    // `expandAll` is review mode's: it walks every page in the period, and the
    // ids it would otherwise have to name are only knowable from a brief — so
    // asking by name cost it a second read of the most expensive call in the
    // app. The Dashboard still expands by name, one package at a time.
    const expand = options.expandAll
      ? 'all'
      : (options.expandedPackageIds ?? []).filter(Boolean).join(',');
    const params: Record<string, string> = {};
    if (key) params['domain'] = key;
    if (expand) params['expand'] = expand;
    return this.http
      .get<{ success: boolean; data: ImpactBriefPayload | null }>(`${this.API_URL}/urls/impact-brief`, {
        params: Object.keys(params).length ? params : undefined
      })
      .pipe(
        map((res) => res?.data ?? null),
        catchError(() => of(null))
      );
  }
}
